import { useForm } from "react-hook-form";
import Button from "../../../../../../library/button";
import { ButtonsWrapper, Field, RenameForm } from "./styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { renameFileSchema, RenameFileSchema } from "./schema";
import { Label } from "../../../../../../library/label";
import Input from "../../../../../../library/input";
import {
  sendErrorNotification,
  sendSuccessNotification,
} from "../../../../../../library/notification";
import useUpdateFilename from "../../../../../../hooks/useUpdateFilename";
import { getIcon } from "../../../../../../utils/get-icon";
import { useMemo } from "react";

interface RenameModalProps {
  videoId: string;
  filename: string;
  onClose: () => void;
}

const RenameModal = ({ videoId, filename, onClose }: RenameModalProps) => {
  const { updateFilename } = useUpdateFilename();

  const { baseName, extension } = useMemo(() => {
    if (!filename) return { baseName: "", extension: "" };
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex < 1) {
      return { baseName: filename, extension: "" };
    }
    return {
      baseName: filename.substring(0, lastDotIndex),
      extension: filename.substring(lastDotIndex),
    };
  }, [filename]);

  const {
    handleSubmit,
    register,
    formState: { errors, isLoading },
  } = useForm<RenameFileSchema>({
    resolver: zodResolver(renameFileSchema),
    defaultValues: {
      filename: baseName,
      videoId: videoId,
    },
  });

  const performRename = async (data: RenameFileSchema) => {
    try {
      const newFullName = data.filename + extension;

      await updateFilename({
        videoId: data.videoId,
        filename: newFullName,
      });
      sendSuccessNotification("Filnavnet ble endret.");
      onClose();
    } catch (e) {
      sendErrorNotification("Kunne ikke endre filnavn.");
      onClose();
    }
  };

  return (
    <RenameForm onSubmit={handleSubmit(performRename)}>
      <Field>
        <Label>Filnavn</Label>
        <Input
          register={register}
          name="filename"
          variant="regular"
          error={errors["filename"]}
        />
      </Field>
      <ButtonsWrapper>
        <Button
          disabled={isLoading}
          type="submit"
          color="blue"
          variant="regular"
        >
          {isLoading ? (
            <>{getIcon("spinner")} Oppdaterer</>
          ) : (
            <>Oppdater filnavn</>
          )}
        </Button>
      </ButtonsWrapper>
    </RenameForm>
  );
};

export default RenameModal;
